import { create } from "zustand";
import { persist } from "zustand/middleware";

export type LanguageCode = "en" | "pt" | "es" | "fr" | "zh";

export interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  tenantId: string;
  currentCompanyId: string | null;
  preferredLanguage: LanguageCode | null;
  companyDefaultLanguage: LanguageCode | null;
  permissions: string[];
  phone: string | null;
  documentNumber: string | null;
  jobTitle: string | null;
  department: string | null;
  timezone: string | null;
  avatarUrl: string | null;
  displayName: string | null;
  namePrefix: string | null;
  pronouns: string | null;
  sidebarStyle: string | null;
}

export interface ImpersonatorInfo {
  id: string;
  email: string;
  name: string;
}

export interface ImpersonateUserResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  impersonator: ImpersonatorInfo;
  permissions: string[];
}

export interface EndImpersonationResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  permissions: string[];
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True when we hold a session but could not reach the API to revalidate it.
   * The app keeps running from the persisted user, which is what lets an
   * installed PWA open without connectivity instead of bouncing the user to
   * the login form. Permissions are a stale snapshot while this is true —
   * the client guard is UX only, the backend authorizes every call.
   */
  isOffline: boolean;

  // Impersonation state
  isImpersonating: boolean;
  impersonator: ImpersonatorInfo | null;

  // Security policy state
  forcedAction: string | null;
  mfaEnforcementWarning: boolean;
  mfaEnforcementDeadline: string | null;

  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setCurrentCompany: (companyId: string) => void;
  setPermissions: (permissions: string[]) => void;
  setPreferredLanguage: (language: LanguageCode | null) => void;
  setLoading: (loading: boolean) => void;
  setOffline: (offline: boolean) => void;
  setForcedAction: (action: string | null) => void;
  setMfaEnforcement: (warning: boolean, deadline: string | null) => void;
  logout: () => void;

  // Impersonation actions
  startImpersonation: (response: ImpersonateUserResponse) => void;
  endImpersonation: (response: EndImpersonationResponse) => void;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      isOffline: false,

      // Impersonation state
      isImpersonating: false,
      impersonator: null,

      // Security policy state
      forcedAction: null,
      mfaEnforcementWarning: false,
      mfaEnforcementDeadline: null,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          isOffline: false,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
        }),

      setCurrentCompany: (companyId) =>
        set((state) => ({
          user: state.user ? { ...state.user, currentCompanyId: companyId } : null,
        })),

      setPermissions: (permissions) =>
        set((state) => ({
          user: state.user ? { ...state.user, permissions } : null,
        })),

      setPreferredLanguage: (language) =>
        set((state) => ({
          user: state.user ? { ...state.user, preferredLanguage: language } : null,
        })),

      setLoading: (isLoading) => set({ isLoading }),

      setOffline: (isOffline) => set({ isOffline }),

      setForcedAction: (forcedAction) => set({ forcedAction }),

      setMfaEnforcement: (mfaEnforcementWarning, mfaEnforcementDeadline) =>
        set({ mfaEnforcementWarning, mfaEnforcementDeadline }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          isOffline: false,
          isImpersonating: false,
          impersonator: null,
          forcedAction: null,
          mfaEnforcementWarning: false,
          mfaEnforcementDeadline: null,
        }),

      // Start impersonation - update state with impersonated user and impersonator info
      startImpersonation: (response: ImpersonateUserResponse) =>
        set({
          user: response.user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          isAuthenticated: true,
          isLoading: false,
          isImpersonating: true,
          impersonator: response.impersonator,
        }),

      // End impersonation - restore original user
      endImpersonation: (response: EndImpersonationResponse) =>
        set({
          user: response.user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          isAuthenticated: true,
          isLoading: false,
          isImpersonating: false,
          impersonator: null,
        }),

      // Permission helper: check if user has a specific permission
      hasPermission: (permission: string) => {
        const user = get().user;
        if (!user || !user.permissions) return false;
        return user.permissions.includes(permission);
      },

      // Permission helper: check if user has any of the permissions
      hasAnyPermission: (permissions: string[]) => {
        const user = get().user;
        if (!user || !user.permissions) return false;
        return permissions.some((p) => user.permissions.includes(p));
      },

      // Permission helper: check if user has all the permissions
      hasAllPermissions: (permissions: string[]) => {
        const user = get().user;
        if (!user || !user.permissions) return false;
        return permissions.every((p) => user.permissions.includes(p));
      },
    }),
    {
      name: "t2c-auth",
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        // Persisted so a cold start — notably an installed PWA opened with no
        // connectivity — can render from the last known user instead of
        // blocking on /auth/me. It sits in the same localStorage blob as the
        // tokens, so this adds no exposure that wasn't already there.
        user: state.user,
        // Also persist impersonation state
        isImpersonating: state.isImpersonating,
        impersonator: state.impersonator,
      }),
      // `isAuthenticated` is derived, not persisted, so without this the store
      // rehydrates with a perfectly good token and `isAuthenticated: false` —
      // and every ProtectedRoute bounces to /login before /auth/me can answer.
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<AuthState>;
        return {
          ...current,
          ...saved,
          isAuthenticated: Boolean(saved.accessToken && saved.user),
        };
      },
    },
  ),
);
